/**
 * CSS
 */
import '../css/index.css';

/**
 * JS
 */
import '@babel/polyfill';
import 'regenerator-runtime/runtime';
import React from 'react';
import ReactDOM from 'react-dom';
import {createMuiTheme, MuiThemeProvider} from '@material-ui/core';
import {pick} from 'lodash/object';
import BentoBox from './BentoBox';
import {createSnapshot} from './SnapshotForm';
import {DataExportFormat, getCurrentDate, isMobile, INSTRUMENT_MAP} from './utils';

window.NationalInstruments = window.NationalInstruments || {};
window.NationalInstruments.bentoBox = window.NationalInstruments.bentoBox || {};
window.NationalInstruments.measurementsLive = window.NationalInstruments.measurementsLive || {};
if (typeof window.NationalInstruments.bentoBox.rootUrl === 'undefined') {
  const thisScript = document.scripts[document.scripts.length - 1];
  const pathParts = thisScript.src.split('/');
  pathParts.pop();
  window.NationalInstruments.bentoBox.rootUrl = pathParts.join('/');
  pathParts.pop();
  pathParts.push('cdn');
  window.NationalInstruments.measurementsLive.rootUrl = process.env.LOBBYBAR_ROOT_URL
    ? `${document.location.origin}${process.env.LOBBYBAR_ROOT_URL}`
    : pathParts.join('/');
}
window.NationalInstruments.bentoBox.svgSpriteUrl = `${window.NationalInstruments.bentoBox.rootUrl}/images/sprite.svg#`;

document.documentElement.classList.toggle('isMobile', isMobile());

window.customElements.define(
  'measurements-live-bento-box',
  class extends HTMLElement {
    constructor() {
      super();
      this._settings = {};
      this.resizeBentoBoxRoot = this.resizeBentoBoxRoot.bind(this);
      this._setSettings = this._setSettings.bind(this);
      this.getSettings = this.getSettings.bind(this);
      this.getSnapshots = this.getSnapshots.bind(this);
      this.getDataExport = this.getDataExport.bind(this);
    }

    connectedCallback() {
      const parameters = new URLSearchParams(location.search);
      let initialSettings = JSON.parse(parameters.get('settings'));
      initialSettings =
        initialSettings === null
          ? this.getAttribute('settings')
            ? JSON.parse(this.getAttribute('settings'))
            : {}
          : initialSettings;
      this.classList.add('bentobox-root');
      ReactDOM.render(
        <MuiThemeProvider
          theme={createMuiTheme({
            typography: {
              useNextVariants: true,
            },
            transitions: {
              create: () => 'none',
            },
            props: {
              MuiButtonBase: {
                disableRipple: true,
              },
            },
          })}
        >
          <BentoBox bentoBoxRoot={this} initialSettings={initialSettings} setSettings={this._setSettings} />
        </MuiThemeProvider>,
        this,
      );
      this.resizeBentoBoxRoot();
      window.addEventListener('resize', this.resizeBentoBoxRoot);
    }

    resizeBentoBoxRoot() {
      const width = this.getAttribute('width') || document.documentElement.clientWidth.toString();
      const height = this.getAttribute('height') || document.documentElement.clientHeight.toString();
      this.style.width = `${width}px`;
      this.style.height = `${height}px`;
      this.style.overflow = 'hidden';
      this.dispatchEvent(new Event('resize'));
    }

    _setSettings(settings) {
      this._settings = {
        ...this._settings,
        ...settings,
      };
    }

    getSettings(encoded = true) {
      const {
        instrumentData: {
          instruments: {large, small, poppedOut},
          order,
          childWindows,
        },
      } = this._settings;
      const instrumentsWrapper = this.querySelector('.bentobox-wrapper .instruments-wrapper');
      const instrumentsInitialSettingsMap = {};
      for (const instrument of [...large, ...small]) {
        const instrumentEl = instrumentsWrapper.querySelector(INSTRUMENT_MAP[instrument.name]);
        if (!instrumentEl.serialize) return;
        instrumentsInitialSettingsMap[instrument.name] = instrumentEl.serialize();
      }
      for (const [instrumentName, childWindow] of Object.entries(childWindows)) {
        const instrumentEl = childWindow.querySelector(INSTRUMENT_MAP[instrumentName]);
        if (!instrumentEl.serialize) return;
        instrumentsInitialSettingsMap[instrumentName] = instrumentEl.serialize();
      }
      const settings = {
        instrumentData: {
          instruments: {
            large,
            small,
            poppedOut,
          },
          order,
          instrumentsInitialSettingsMap,
        },
      };
      return encoded ? JSON.stringify(settings) : settings;
    }

    getSnapshots(instrumentNames) {
      if (!Array.isArray(instrumentNames)) {
        throw Error('A list of instrument names must be provided to getSnapshots');
      }
      const instrumentsWrapper = this.querySelector('.bentobox-wrapper .instruments-wrapper');
      const {year, month, day, hour, minute, second} = getCurrentDate();
      const dateStr = `${month}/${day}/${year}`;
      const timeStr = `${hour}:${minute}:${second}`;
      return Promise.all(
        instrumentNames.map((instrumentName) => {
          const instrumentEl = instrumentsWrapper.querySelector(INSTRUMENT_MAP[instrumentName]);
          return !instrumentEl || !instrumentEl.getMeasurementsData
            ? Promise.resolve(null)
            : Promise.all([instrumentEl.getMeasurementsData()])
                .then(([result]) => createSnapshot(result, instrumentName, dateStr, timeStr))
                .then(
                  ({canvas, name}) =>
                    new Promise((resolve) => {
                      canvas.toBlob((blob) => resolve({name, blob}));
                    }),
                );
        }),
      ).then((results) => {
        const blobMap = {};
        for (const {name, blob} of results.filter((result) => result != null)) {
          blobMap[name] = blob;
        }
        return blobMap;
      });
    }

    getDataExport(exportEntries) {
      if (!Array.isArray(exportEntries)) {
        throw Error('A list of export entries names must be provided to getDataExport');
      }
      const instrumentsWrapper = this.querySelector('.bentobox-wrapper .instruments-wrapper');
      return Promise.all(
        Array.from(exportEntries).map((exportEntry) => {
          const {name, format} = exportEntry;
          const instrumentEl = instrumentsWrapper.querySelector(INSTRUMENT_MAP[name]);
          return instrumentEl.exportDataAsync
            ? Promise.all([name, format, instrumentEl.exportDataAsync(DataExportFormat[format], name)])
            : Promise.resolve(null);
        }),
      )
        .then((results) => results.filter((result) => result != null))
        .then((results) => {
          const ret = [];
          for (const [name, format, blob] of results) {
            ret.push([name, format, blob]);
          }
          return ret;
        });
    }
  },
);
